import React from 'react';
import { Compass, Ship, Package, Globe, ArrowRight, BookOpen, Clock } from 'lucide-react';
import { ArchiveTab } from '../../types';
import { ArchivalImageCard } from '../ArchivalImageCard';

interface JourneyHubProps {
  onNavigate: (tab: ArchiveTab) => void;
}

export const JourneyHubSection: React.FC<JourneyHubProps> = ({ onNavigate }) => {
  const chapters = [
    {
      id: 'journey-suffolk' as ArchiveTab,
      title: 'From Suffolk to the Southern Seas',
      subtitle: 'The 1850 Voyage & Suffolk Roots',
      description: 'The 99-day ocean crossing of John and Anne Denton with their seven children, life in Kersey and Great Barton, the verbatim letters of 1850–1851, and the tragic fever of 1851.',
      icon: <Ship className="w-6 h-6 text-[#7a5b3a]" />,
      image: '/images/journey/kersey.jpg',
      imageAlt: 'Kersey, Suffolk',
      stats: 'Suffolk Letters • 1850 Voyage • Parish Headstones'
    },
    {
      id: 'journey-essentials' as ArchiveTab,
      title: 'Essentials for a new land',
      subtitle: '1850 Outfitting & Provisions',
      description: 'Detailed historical inventory of what 1850 colonists brought: women’s dresses and chemises, men’s fustian jackets and duck trousers, Louisa Alice Denton’s wooden toys, seed stocks, and prefabricated cottages.',
      icon: <Package className="w-6 h-6 text-[#7a5b3a]" />,
      image: '/images/13 cream dress.jpg',
      imageAlt: 'Colonial Outfitting & Dresses',
      stats: 'Wardrobe Lists • Farm Implements • Seed Varieties'
    },
    {
      id: 'journey-why-they-came' as ArchiveTab,
      title: 'Why they came around the curve of the Earth',
      subtitle: 'Colonial Motivations & Early Settlement',
      description: 'The economic pressures of 1840s Britain, the Canterbury Association and Edward Gibbon Wakefield, steerage conditions, shorthorn cattle on deck, Kemp’s Deed (1848), and arrival at Lyttelton port.',
      icon: <Globe className="w-6 h-6 text-[#7a5b3a]" />,
      image: '/images/27 Steerage.jpg',
      imageAlt: 'Steerage Emigrant Ship',
      stats: 'Historical Context • Lyttelton 1850s • Early Plains'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-12 pb-16 font-sans text-[#2c221e]">
      
      {/* Hub Page Header */}
      <header className="bg-[#f3ede0] p-8 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm relative overflow-hidden space-y-4">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#e8dec9] text-[#7a5b3a] text-xs font-serif font-bold tracking-wider uppercase border border-[#d8ceb9]">
          <Compass className="w-3.5 h-3.5 text-[#7a5b3a]" />
          <span>The Passage to Canterbury</span>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight">
          The Journey
        </h1>

        <p className="font-serif text-base md:text-xl text-[#7a5b3a] italic leading-relaxed border-l-4 border-[#7a5b3a] pl-4 my-2">
          An archival exploration of the 1850 migration from rural Suffolk across the oceans to the Canterbury settlement, documented through letters, records, and preserved artifacts.
        </p>
      </header>

      {/* Chapter Selection Grid */}
      <div className="space-y-6">
        <div className="border-b-2 border-[#d8ceb9] pb-3 flex items-center justify-between">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            Chapters in this Collection
          </h2>
          <span className="text-xs font-serif text-[#7a5b3a] italic hidden sm:inline">
            Select a chapter below
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {chapters.map((chapter) => (
            <div
              key={chapter.id}
              onClick={() => onNavigate(chapter.id)}
              className="group bg-[#fbf9f4] p-6 sm:p-8 rounded-3xl border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md transition cursor-pointer flex flex-col md:flex-row gap-6 items-start justify-between"
            >
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-[#f3ede0] border border-[#d8ceb9] group-hover:bg-[#e8dec9] transition shrink-0">
                    {chapter.icon}
                  </div>
                  <div>
                    <span className="text-xs font-serif font-bold uppercase tracking-wider text-[#7a5b3a]">
                      {chapter.subtitle}
                    </span>
                    <h3 className="font-serif text-2xl font-bold text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                      {chapter.title}
                    </h3>
                  </div>
                </div>

                <p className="text-base text-[#4a3525] leading-relaxed">
                  {chapter.description}
                </p>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                  <span className="text-xs font-serif text-[#7a5b3a] italic bg-[#e8dec9]/50 px-3 py-1 rounded-full border border-[#d8ceb9]/60">
                    {chapter.stats}
                  </span>

                  <span className="inline-flex items-center gap-1.5 text-sm font-serif font-bold text-[#7a5b3a] group-hover:text-[#4a3525] transition">
                    <span>Read Chapter</span>
                    <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Archival Overview Card */}
      <article className="bg-[#fbf9f4] p-8 md:p-10 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-6">
        <h3 className="font-serif text-2xl font-bold text-[#2c221e] border-b border-[#d8ceb9] pb-2">
          Historical Overview of the 1850 Canterbury Migration
        </h3>
        
        <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
          The Canterbury Association was founded in London in 1848 by John Robert Godley and Edward Gibbon Wakefield with the vision of establishing an organized Church of England settlement in the South Island of New Zealand. The Dentons were among the early pioneer families who answered the call, sailing 99 days aboard small sailing vessels to arrive in Lyttelton in December 1850.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
          <ArchivalImageCard
            src="/images/journey/st-nicholas-suffolk.jpg"
            alt="St Nicholas, Suffolk"
            caption="St Nicholas, Suffolk — the ancestral parish"
            filenameHint="st-nicholas-suffolk.jpg"
          />

          <ArchivalImageCard
            src="/images/journey/cathedral-square-christchurch.jpg"
            alt="Cathedral Square, Christchurch"
            caption="Cathedral Square, Christchurch in early settlement years"
            filenameHint="cathedral-square-christchurch.jpg"
          />
        </div>
      </article>

    </div>
  );
};
